'use client'

import type { ComponentProps } from 'react'

import { cn } from '@morpho-org/ui-v6/utils'
import { Badge, Toggletip, ToggletipContent, ToggletipTrigger } from '@repo/ui'

export interface V1BadgeProps extends Omit<
  ComponentProps<typeof Badge>,
  'children' | 'size' | 'variant'
> {
  /** "s" (27x18) and "l" (50x34) keep the dimensions the retired V2Badge used. */
  size?: 's' | 'l'
}

/**
 * Neutral "V1" pill marking a legacy Morpho Vault V1. Deliberately quieter than
 * the unbadged V2 vaults, which stay the preferred option.
 *
 * Focusable so the tooltip is reachable by keyboard. Pass `tabIndex={-1}` where
 * the badge sits inside a link, so a list does not gain a tab stop per row.
 */
const V1Badge = ({ className, size = 's', ...props }: V1BadgeProps) => (
  <Toggletip>
    <ToggletipTrigger asChild>
      <Badge
        variant="tertiary"
        size="sm"
        role="button"
        tabIndex={0}
        aria-label="Morpho Vault V1"
        className={cn(
          'px-0',
          size === 's' ? 'h-[18px] w-[27px] font-medium' : 'h-[34px] w-[50px] text-lg',
          className
        )}
        {...props}
      >
        V1
      </Badge>
    </ToggletipTrigger>
    <ToggletipContent>Uses the legacy Morpho Vault V1 architecture</ToggletipContent>
  </Toggletip>
)

export { V1Badge }
